// Commitment Kanban column — FE-042 / FE-042b.
//
// One vertical stack of cards for a given Commitment status. Mirrors
// the column header treatment in the Pencil ``BeneCRM Tasks`` frame:
// status label + count, fixed column width on wider viewports, body
// stacks cards with a small gap and falls back to an "honest" empty
// state when the column has nothing in it.
//
// Two render paths:
//
//   * Static (default) — caller passes pre-fetched ``rows`` (legacy
//     board path; also used by every kanban unit test). No
//     network, no QueryClient required.
//
//   * Self-fetching — caller passes ``bucketFetch`` and the column
//     owns its data via ``useCommitmentBucketInfiniteQuery``, with
//     IntersectionObserver-driven lazy loading inside the column's
//     own scroll viewport. This is the path the Tracker page wires
//     up so each board column keeps scrolling as the user reaches
//     the bottom.
//
// The hook lives only inside the self-fetching subcomponent so
// callers / tests on the static path don't have to wrap in a
// QueryClientProvider.

import { useEffect, useMemo, useRef, useState } from "react";

import {
  CommitmentKanbanCard,
  KANBAN_DRAG_MIME,
} from "./CommitmentKanbanCard";
import { COMMITMENT_STATUS_COLUMN_META } from "./commitmentStatusMeta";
import { useCommitmentBucketInfiniteQuery } from "./hooks";
import type { GroupedFetchInputs } from "./CommitmentListTable";
import type { CommitmentListRow, CommitmentStatus } from "./types";


export interface CommitmentKanbanColumnProps {
  status: CommitmentStatus;
  /** Pre-fetched rows for legacy callers / tests that hand the
   * column its rows directly. Ignored when ``bucketFetch`` is set. */
  rows: CommitmentListRow[];
  /** When provided, the column owns its data: it runs its own
   * ``useCommitmentBucketInfiniteQuery`` for ``status`` and
   * lazy-loads more rows on column scroll via IntersectionObserver.
   * Without it the board capped at the page-level ``rows`` slice
   * (~200 cards across all columns) and ran out of cards mid-scroll
   * on big trackers. */
  bucketFetch?: GroupedFetchInputs;
  /** Full-filter total for this status, surfaced from the BE list
   * response. Falls back to ``rows.length`` when omitted. */
  totalForStatus?: number;
  /** Forwarded to each card so the kebab menu can pop up; also the
   * drop handler routes through the same callback when a card is
   * dragged onto this column. */
  onMove?: (commitmentId: string, toStatus: CommitmentStatus) => void;
  onAssign?: (commitmentId: string, userId: string | null) => void;
  onSetDueDate?: (commitmentId: string, isoOrNull: string | null) => void;
  members?: import("./api").MemberPickerRow[];
  visibleStatuses?: CommitmentStatus[];
  pendingCommitmentId?: string | null;
  buildLinkState?: () => unknown;
}


export function CommitmentKanbanColumn(props: CommitmentKanbanColumnProps) {
  // The fetching path runs an extra hook; isolating it inside a
  // dedicated subcomponent keeps the static path hook-free so
  // existing tests don't have to wrap in a QueryClientProvider.
  if (props.bucketFetch) {
    return <FetchingColumn {...props} bucketFetch={props.bucketFetch} />;
  }
  return <ColumnShell {...props} rowsForDisplay={props.rows} />;
}


function FetchingColumn(
  props: CommitmentKanbanColumnProps & { bucketFetch: GroupedFetchInputs },
) {
  const { status, bucketFetch, totalForStatus } = props;
  const baseFilters = useMemo(
    () => ({
      ...bucketFetch.filters,
      ...(status === "cancelled" ? { include_cancelled: true } : {}),
    }),
    [bucketFetch, status],
  );
  const bucketQuery = useCommitmentBucketInfiniteQuery({
    status,
    base: {
      filters: baseFilters,
      filterTree: bucketFetch.filterTree,
      sortBy: bucketFetch.sortBy,
      sortDir: bucketFetch.sortDir,
    },
    enabled: (totalForStatus ?? 0) > 0,
  });
  const fetchedRows: CommitmentListRow[] = (bucketQuery.data?.pages ?? [])
    .flatMap((p) => p.rows);

  // IntersectionObserver root is the column's own scrolling
  // ``<ul>``; the sentinel ``<li>`` at the bottom of the list
  // triggers ``fetchNextPage`` as soon as the user scrolls within
  // 200px of the end.
  const ulRef = useRef<HTMLUListElement | null>(null);
  const sentinelRef = useRef<HTMLLIElement | null>(null);
  useEffect(() => {
    const node = sentinelRef.current;
    const root = ulRef.current;
    if (!node || !root) return;
    if (!bucketQuery.hasNextPage || bucketQuery.isFetchingNextPage) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          bucketQuery.fetchNextPage();
        }
      },
      { root, rootMargin: "200px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [
    bucketQuery.hasNextPage,
    bucketQuery.isFetchingNextPage,
    bucketQuery.fetchNextPage,
    fetchedRows.length,
  ]);

  return (
    <ColumnShell
      {...props}
      rowsForDisplay={fetchedRows}
      isLoading={bucketQuery.isLoading}
      ulRef={ulRef}
      sentinelRef={sentinelRef}
      showSentinel={bucketQuery.hasNextPage}
      sentinelLabel={bucketQuery.isFetchingNextPage ? "Loading…" : ""}
    />
  );
}


interface ColumnShellProps extends CommitmentKanbanColumnProps {
  rowsForDisplay: CommitmentListRow[];
  isLoading?: boolean;
  ulRef?: React.MutableRefObject<HTMLUListElement | null>;
  sentinelRef?: React.MutableRefObject<HTMLLIElement | null>;
  showSentinel?: boolean;
  sentinelLabel?: string;
}


function ColumnShell({
  status, rows, rowsForDisplay, isLoading, totalForStatus,
  onMove, onAssign, onSetDueDate, members,
  visibleStatuses, pendingCommitmentId, buildLinkState,
  ulRef, sentinelRef, showSentinel = false, sentinelLabel = "",
}: ColumnShellProps) {
  const meta = COMMITMENT_STATUS_COLUMN_META[status];
  const [dragOver, setDragOver] = useState(false);

  function readDragPayload(
    dt: DataTransfer,
  ): { commitment_id: string; from_status: CommitmentStatus } | null {
    const structured = dt.getData(KANBAN_DRAG_MIME);
    if (structured) {
      try {
        const parsed = JSON.parse(structured);
        if (
          parsed
          && typeof parsed.commitment_id === "string"
          && typeof parsed.from_status === "string"
        ) {
          return parsed;
        }
      } catch {
        // fall through to text/plain
      }
    }
    const id = dt.getData("text/plain");
    if (id) return { commitment_id: id, from_status: status };
    return null;
  }

  return (
    <section
      data-testid={`commitments-column-${status}`}
      data-status={status}
      data-drag-over={dragOver ? "true" : "false"}
      aria-label={meta.label}
      onDragOver={(e) => {
        if (!onMove) return;
        // Only accept drags that look like our kanban cards; ignoring
        // the event by skipping ``preventDefault`` keeps the drop
        // gesture invalid and the cursor reverts to the not-allowed
        // icon.
        const types = Array.from(e.dataTransfer.types);
        if (!types.includes(KANBAN_DRAG_MIME) && !types.includes("text/plain")) {
          return;
        }
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (!dragOver) setDragOver(true);
      }}
      onDragLeave={(e) => {
        // Only clear the highlight when the cursor actually leaves
        // the column box, not when it crosses into a child element.
        if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
        setDragOver(false);
      }}
      onDrop={(e) => {
        if (!onMove) return;
        const payload = readDragPayload(e.dataTransfer);
        setDragOver(false);
        if (!payload) return;
        e.preventDefault();
        if (payload.from_status === status) return;
        onMove(payload.commitment_id, status);
      }}
      // Each swimlane fills the parent height (``h-full``) and lets
      // its inner ``<ul>`` scroll independently via
      // ``min-h-0 overflow-y-auto``. Combined with the board's
      // ``flex-1 min-h-0`` wrapper, this makes the board span the
      // viewport instead of collapsing to fit-content.
      className={[
        "flex h-full w-64 min-h-0 flex-shrink-0 flex-col gap-2 rounded-md p-1 transition-fast ease-out",
        dragOver ? "bg-teal-tint/40 ring-1 ring-teal/40" : "",
      ].join(" ")}
    >
      {/* Pencil column header — title-case label inline with a small
          plain count, no chip wrapper, no UPPERCASE, no card-style
          background panel around the column. The accent dot stays
          ahead of the label so each lane is recognisable at a
          glance, but the typography stops competing with the cards. */}
      <header className="flex items-center gap-2 px-1 pt-1">
        <span
          aria-hidden="true"
          className={[
            "inline-block h-1.5 w-1.5 rounded-full",
            meta.accentDot,
          ].join(" ")}
        />
        <h3 className="text-sm font-semibold tracking-tight text-text-primary">
          {meta.label}
        </h3>
        <span
          data-testid={`commitments-column-count-${status}`}
          className="text-xs font-normal text-text-tertiary"
        >
          {totalForStatus ?? rows.length}
        </span>
      </header>

      {rowsForDisplay.length === 0 && !isLoading ? (
        <div
          data-testid={`commitments-column-empty-${status}`}
          className="rounded-md border border-dashed border-border-subtle bg-raised/30 px-3 py-6 text-center text-xs text-text-tertiary"
        >
          No Commitments
        </div>
      ) : (
        <ul
          ref={ulRef}
          role="list"
          className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-1 pb-1"
        >
          {rowsForDisplay.map((row) => (
            <li key={row.commitment_id}>
              <CommitmentKanbanCard
                row={row}
                onMove={onMove}
                onAssign={onAssign}
                onSetDueDate={onSetDueDate}
                members={members}
                visibleStatuses={visibleStatuses}
                isPending={pendingCommitmentId === row.commitment_id}
                linkState={buildLinkState ? buildLinkState() : undefined}
              />
            </li>
          ))}
          {/* Sentinel — IntersectionObserver in the parent fires
           * ``fetchNextPage`` when this row enters the column's
           * scroll viewport. Only rendered when more pages remain. */}
          {showSentinel && (
            <li
              ref={sentinelRef}
              data-testid={`commitments-column-sentinel-${status}`}
              className="px-1 py-2 text-center text-xs text-text-tertiary"
            >
              {sentinelLabel}
            </li>
          )}
        </ul>
      )}
    </section>
  );
}
